/**
 * Calculator Page - Main Orchestrator Component
 * 
 * Refactored modular structure:
 * - State management via useReducer (useCalculatorState)
 * - Action handlers in custom hook (useCalculatorActions)
 * - SEO management in custom hook (useCalculatorSEO)
 * - Presentational components for UI sections
 */
import { useEffect, useRef, useState } from 'react';
import { Container, Stack, Alert, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { getCurrentUser } from 'aws-amplify/auth';
import { useLocation, useParams } from 'react-router-dom';
import { Layout } from '../../shared/components';
import { useAuth } from '../auth';
import { useOSD } from '../../contexts/OSDContext';
import { getSavedCalculationByYear } from '../../lib/api/savedCalculationApi';
import { useAnalytics } from '../../shared/hooks';
import { parseOSDFromUrl, parseTariffFromUrl } from '../../utils/seoUrlUtils';
import { useCalculatorState } from './hooks/useCalculatorState';
import { useCalculatorSEO } from './hooks/useCalculatorSEO';
import { useCalculatorActions } from './hooks/useCalculatorActions';
import { CalculatorHeader } from './components/CalculatorHeader';
import { CalculatorFormSection } from './components/CalculatorFormSection';
import { CalculatorResultsSection } from './components/CalculatorResultsSection';
import { CalculatorActions } from './components/CalculatorActions';
import type { OSDCode } from '../../types/osd.types';
import type { OSDCode as OSDMetaCode } from '../../utils/osdMetadata';
import type { CalculatorFormValues } from './types/calculator.types';

/**
 * Calculator Page Component
 * 
 * Main orchestrator that coordinates:
 * - State management (useReducer pattern)
 * - Action handling (calculate, save, share)
 * - SEO optimization (title, meta, canonical)
 * - Effect orchestration (data loading, URL params, analytics)
 */
export function Calculator() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { currentOSD } = useOSD();
  const { osd: osdParam, tariff: tariffParam } = useParams<{ osd?: string; tariff?: string }>();
  const analytics = useAnalytics();
  const resultsRef = useRef<HTMLDivElement>(null);

  // UX-005: Track estimation data from QuickCalculator
  const [estimationInfo, setEstimationInfo] = useState<{
    estimatedConsumption: number;
    sourceMonthlyBill: number;
  } | null>(null);

  // Centralized state management
  const { state, dispatch } = useCalculatorState();

  // Parse URL parameters
  const urlOSD = parseOSDFromUrl(osdParam);
  const urlTariff = urlOSD ? parseTariffFromUrl(tariffParam, urlOSD) : null;

  // SEO management
  useCalculatorSEO({
    urlOSD,
    urlTariff,
    selectedOSD: state.selectedOSD,
    currentTariff: state.currentTariff,
  });

  // Action handlers
  const { handleCalculate, handleBaselineTariffChange, handleSaveCalculation } = useCalculatorActions({
    dispatch,
    selectedOSD: state.selectedOSD,
    calculationYear: state.calculationYear,
    initialSharedData: state.initialSharedData,
    resultsRef,
  });

  // Apply URL params OR context OSD to calculator state on mount
  useEffect(() => {
    const osdToSet = urlOSD || currentOSD;
    if (osdToSet && !state.urlParamsApplied) {
      dispatch({ type: 'SET_SELECTED_OSD', payload: osdToSet });
      if (urlTariff) {
        dispatch({ type: 'SET_CURRENT_TARIFF', payload: urlTariff });
        dispatch({ type: 'SET_BASELINE_TARIFF', payload: urlTariff });
      }
      dispatch({ type: 'SET_URL_PARAMS_APPLIED', payload: true });
    }
  }, [urlOSD, urlTariff, currentOSD, state.urlParamsApplied, dispatch]);

  // GA4: Track calculator page view
  useEffect(() => {
    analytics.trackCalculatorStarted();
  }, [analytics]);

  // Map saved calculation OR shared calculation to form values on mount
  useEffect(() => {
    const savedCalculation = location.state?.savedCalculation;
    const sharedCalculation = location.state?.sharedCalculation;
    const osdFromQuickCalc = location.state?.selectedOSD;

    // UX-005: Extract estimation data from QuickCalculator
    const estimatedConsumption = location.state?.estimatedConsumption as number | undefined;
    const sourceMonthlyBill = location.state?.sourceMonthlyBill as number | undefined;
    const isEstimated = location.state?.isEstimated as boolean | undefined;

    // Set OSD from QuickCalculator if passed
    if (osdFromQuickCalc && ['TAURON', 'PGE', 'ENEA', 'ENERGA'].includes(osdFromQuickCalc)) {
      dispatch({ type: 'SET_SELECTED_OSD', payload: osdFromQuickCalc as OSDCode });
    }

    // UX-005: Handle estimated consumption from QuickCalculator
    if (isEstimated && estimatedConsumption && sourceMonthlyBill && !savedCalculation && !sharedCalculation) {
      // Store estimation info for the alert
      setEstimationInfo({
        estimatedConsumption,
        sourceMonthlyBill,
      });

      // Pre-fill the form with estimated monthly consumption (divide annual by 12)
      const monthlyEstimate = Math.round(estimatedConsumption / 12);
      const mappedValues: CalculatorFormValues = {
        monthlyConsumption: Array(12).fill(monthlyEstimate),
        currentTariff: 'G11',
        householdSize: 4,
        hasHeatPump: false,
        hasSolar: false,
        hasEV: false,
        consumptionPattern: 'UNIFORM',
        hourlyConfig: {
          weekday: Array(24).fill(1 / 24),
          weekend: Array(24).fill(1 / 24),
        },
      };
      dispatch({ type: 'SET_INITIAL_FORM_VALUES', payload: mappedValues });
    }

    if (savedCalculation) {
      const mappedValues: CalculatorFormValues = {
        monthlyConsumption: savedCalculation.monthlyConsumption || Array(12).fill(292),
        currentTariff: 'G11',
        householdSize: savedCalculation.householdSize || 4,
        hasHeatPump: savedCalculation.hasHeatPump || false,
        hasSolar: savedCalculation.hasSolarPanels || false,
        hasEV: savedCalculation.hasElectricVehicle || false,
        consumptionPattern: 'UNIFORM',
        hourlyConfig: savedCalculation.hourlyPattern || {
          weekday: Array(24).fill(1 / 24),
          weekend: Array(24).fill(1 / 24),
        },
      };
      dispatch({ type: 'SET_INITIAL_FORM_VALUES', payload: mappedValues });
    } else if (sharedCalculation) {
      dispatch({ type: 'SET_INITIAL_FORM_VALUES', payload: sharedCalculation });

      if (sharedCalculation.selectedOSD && ['TAURON', 'PGE', 'ENEA', 'ENERGA'].includes(sharedCalculation.selectedOSD)) {
        dispatch({ type: 'SET_SELECTED_OSD', payload: sharedCalculation.selectedOSD as OSDCode });
      }

      dispatch({ type: 'SET_INITIAL_SHARED_DATA', payload: sharedCalculation });

      const existingHash = location.state?.existingShareHash;
      const sharedOSD = sharedCalculation.selectedOSD || 'TAURON';
      if (existingHash) {
        dispatch({ type: 'SET_SHARE_HASH', payload: existingHash });
        dispatch({ type: 'SET_SHARE_URL', payload: `https://taryfypradu.pl/calc/${state.calculationYear}/${sharedOSD}/${existingHash}` });
        window.history.replaceState(null, '', `/calc/${state.calculationYear}/${sharedOSD}/${existingHash}`);
      }
    }
  }, [location.state, state.calculationYear, dispatch]);

  // Load saved calculation data from API when user is authenticated
  useEffect(() => {
    const loadSavedCalculation = async () => {
      if (isAuthenticated && !location.state?.savedCalculation && !state.initialFormValues) {
        try {
          const currentUser = await getCurrentUser();
          const currentYear = new Date().getFullYear();
          const savedCalc = await getSavedCalculationByYear(currentUser.userId, currentYear);

          if (savedCalc) {
            const monthlyConsumption = typeof savedCalc.monthlyConsumption === 'string'
              ? JSON.parse(savedCalc.monthlyConsumption)
              : savedCalc.monthlyConsumption;

            const hourlyPattern = typeof savedCalc.hourlyPattern === 'string'
              ? JSON.parse(savedCalc.hourlyPattern)
              : savedCalc.hourlyPattern;

            const mappedValues: CalculatorFormValues = {
              monthlyConsumption: monthlyConsumption || Array(12).fill(292),
              currentTariff: 'G11',
              householdSize: savedCalc.householdSize || 4,
              hasHeatPump: savedCalc.hasHeatPump || false,
              hasSolar: savedCalc.hasSolarPanels || false,
              hasEV: savedCalc.hasElectricVehicle || false,
              consumptionPattern: 'UNIFORM',
              hourlyConfig: hourlyPattern || {
                weekday: Array(24).fill(1 / 24),
                weekend: Array(24).fill(1 / 24),
              },
            };

            dispatch({ type: 'SET_INITIAL_FORM_VALUES', payload: mappedValues });
          }
        } catch (err) {
          // Don't set error - this is optional data
        }
      }
    };

    void loadSavedCalculation();
  }, [isAuthenticated, location.state, state.initialFormValues, dispatch]);

  return (
    <Layout isAuthenticated={false}>
      <Container
        size="lg"
        py={40}
        px={{ base: 'md', sm: 'lg' }}
        style={{ width: '100%' }}
      >
        <Stack gap="xl">
          {/* Header Section */}
          <CalculatorHeader
            selectedOSD={state.selectedOSD}
            onOSDChange={(osd) => dispatch({ type: 'SET_SELECTED_OSD', payload: osd })}
            calculationYear={state.calculationYear}
            onYearChange={(year) => dispatch({ type: 'SET_CALCULATION_YEAR', payload: year })}
            loading={state.loading}
            osdFromContext={currentOSD}
          />

          {/* UX-005: Estimation Info Alert from QuickCalculator */}
          {estimationInfo && (
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
              withCloseButton
              onClose={() => setEstimationInfo(null)}
              title="Dane wstępnie uzupełnione"
            >
              <Text size="sm">
                Szacowane roczne zużycie ({estimationInfo.estimatedConsumption.toLocaleString('pl-PL')} kWh)
                zostało obliczone na podstawie miesięcznego rachunku {estimationInfo.sourceMonthlyBill} zł.
                Możesz dostosować te wartości do swoich rzeczywistych danych.
              </Text>
            </Alert>
          )}

          {/* Form Section */}
          <CalculatorFormSection
            onSubmit={handleCalculate}
            loading={state.loading}
            error={state.error}
            onErrorClose={() => dispatch({ type: 'SET_ERROR', payload: null })}
            initialValues={state.initialFormValues || undefined}
            selectedOSD={state.selectedOSD as OSDMetaCode}
            calculationYear={state.calculationYear}
          />

          {/* Results Section */}
          {state.results && !state.loading && (
            <CalculatorResultsSection
              results={state.results}
              currentTariff={state.currentTariff}
              baselineTariff={state.baselineTariff}
              onBaselineTariffChange={(tariff) => handleBaselineTariffChange(tariff, state.baselineTariff)}
              selectedOSD={state.selectedOSD as OSDMetaCode}
              calculationYear={state.calculationYear}
              resultsRef={resultsRef}
            />
          )}

          {/* Actions Section */}
          {state.results && !state.loading && (
            <CalculatorActions
              formValues={state.formValues}
              results={state.results}
              isAuthenticated={isAuthenticated}
              onSave={() => handleSaveCalculation(state.formValues!, state.results!)}
              saveStatus={state.saveStatus}
              saveError={state.saveError}
              shareUrl={state.shareUrl}
              calculationYear={state.calculationYear}
            />
          )}
        </Stack>
      </Container>
    </Layout>
  );
}
